import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Label } from "@sablier/v2-components/molecules";
import { Input } from "@sablier/v2-components/organisms";
import { tags } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _, dayjs } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import { useModalMomentPicker } from "~/client/hooks";
import type { IMilliseconds } from "@sablier/v2-types";
import Suggested from "../Suggested";
import Calendar from "./Calendar";
import Time from "./Time";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 2);
    padding-inline: ${(props) => props.theme.sizes.modalEdge};
    padding-top: ${(props) => props.theme.sizes.edge};
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
  }
`;

const Preview = styled.div`
  width: 100%;
  & > * {
    width: 100%;
  }
`;

const Form = styled.div`
  display: grid;
  grid-template-columns: auto 2px 100px;
  column-gap: calc(${(props) => props.theme.sizes.edge} * 1);
`;

const Left = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    height: 100%;
  }
`;

const Picker = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    align-items: flex-start;
    width: 100%;
    height: 361px;
    column-gap: calc(${(props) => props.theme.sizes.edge} * 1);
  }
`;

const Right = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    height: 364px;
  }
`;

const Divider = styled.div`
  width: 2px;
  height: 100%;
  background-color: ${(props) => props.theme.colors.border};
  &[data-horizontal="true"] {
    width: 100%;
    height: 2px;
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);

    ${Form} {
      grid-template-columns: 1fr;
      gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      & > * {
        grid-column: span 1;
      }
    }
    ${Picker} {
      ${(props) => props.theme.styles.column}
      & {
        width: 100%;
        height: auto;
        & > *:last-child {
          height: 260px;
        }
      }
    }
    ${Right} {
      order: 1;
      width: 100%;
      height: auto;
    }
    ${Left} {
      order: 2;
      width: 100%;
    }
    ${Divider} {
      display: none;
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    padding-top: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
  }
`;
interface Props {
  moment: [string | undefined, (value?: string) => void];
}

function Content({ moment }: Props) {
  const [value, setValue] = moment;
  const { data } = useModalMomentPicker();
  const { t } = useT();

  const purpose = useMemo(
    () => data.purpose || t("structs.dateTime"),
    [data, t],
  );

  const formatted = useMemo(() => {
    if (!_.isNilOrEmptyString(value)) {
      const result = _.toDayjs(value);
      if (result.isValid()) {
        return result;
      }
    }

    return undefined;
  }, [value]);

  const onChangeDate = useCallback(
    (milliseconds?: IMilliseconds) => {
      const date = _.toDayjs(milliseconds);
      const result = date
        .hour(formatted ? formatted.hour() : 0)
        .minute(formatted ? formatted.minute() : 0)
        .second(0);

      setValue(result.valueOf().toString());
    },
    [formatted, setValue],
  );

  const onChangeTime = useCallback(
    (value: number, unit: "hour" | "minute") => {
      try {
        let result = formatted ?? dayjs().second(0);

        if (unit === "hour") {
          result = result.hour(value);
          if (_.isNilOrEmptyString(formatted)) {
            result = result.minute(0);
          }
        } else if (unit === "minute") {
          result = result.minute(value);
          if (_.isNilOrEmptyString(formatted)) {
            result = result.hour(0);
          }
        }

        setValue(result.valueOf().toString());
      } catch (error) {
        vendors.crash.log(error);
      }
    },
    [formatted, setValue],
  );

  return (
    <Wrapper data-cy={tags.modalMomentPicker}>
      <Preview>
        <Input.Moment
          id={"moment"}
          isPreview
          isDisabled
          label={{ value: `${t("words.preview")} ${purpose.toLowerCase()}` }}
          placeholder={_.toDuration(new Date().valueOf(), "date")[0]}
          value={value}
        />
      </Preview>
      <Form>
        <Left>
          <Label value={`${t("words.choose")} ${purpose.toLowerCase()}`} />
          <Picker>
            <Calendar value={formatted} onChange={onChangeDate} />
            <Time value={formatted} onChange={onChangeTime} />
          </Picker>
        </Left>

        <Divider />
        <Right>
          <Suggested setValue={moment[1]} />
        </Right>
      </Form>
    </Wrapper>
  );
}

export default Content;
